import { isProcessedPackageActive, type ProcessedPackage } from "./processedCatalog";
import { isPackagingLanguage, isValidDate, type PackagingLanguage } from "./processedStockModel";

export interface BatchLabelSettings {
  widthMm: number;
  heightMm: number;
  backgroundColor: string;
  accentColor: string;
  textColor: string;
}
export const BATCH_LABEL_COLORS = { backgroundColor: "#ffffff", accentColor: "#2f7d61", textColor: "#18312b" };
export const A4_MM = { width: 210, height: 297 };
export const MAX_BATCH_LABELS = 2000;
export const isLabelColor = (value: unknown): value is string => typeof value === "string" && /^#[\da-f]{6}$/i.test(value);
export function isBatchLabelSettings(value: unknown): value is BatchLabelSettings {
  if (!value || typeof value !== "object") return false;
  const settings = value as BatchLabelSettings;
  return Number.isFinite(settings.widthMm) && settings.widthMm >= 40 && Number.isFinite(settings.heightMm) && settings.heightMm >= 20 &&
    ((settings.widthMm <= 105 && settings.heightMm <= 148.5) || (settings.widthMm <= 148.5 && settings.heightMm <= 105)) &&
    [settings.backgroundColor, settings.accentColor, settings.textColor].every(isLabelColor);
}
export interface LabelStock {
  firebaseId: string; packageId?: string; productId?: string; batchId: string; quantity?: number;
  origin_sv?: string; origin_en?: string;
  packagingLanguage?: PackagingLanguage;
  packingDate?: string; bestBeforeDate?: string; shopSaleStatus?: string;
}
export interface LabelProduct { firebaseId: string; id: string; name_sv: string; name_en: string }
export type LabelLanguage = PackagingLanguage;
export type LabelQuantities = Record<string, string>;
export interface StockLabelOption { stock: LabelStock; pkg: ProcessedPackage; product: LabelProduct }
export const stockLabelOptionId = (option: StockLabelOption) => option.pkg.packageType === "box"
  ? `${option.stock.firebaseId}::${option.pkg.firebaseId}` : option.stock.firebaseId;
export interface BatchPrintLabel {
  stockId: string; language: LabelLanguage; productName: string; origin: string;
  batchNumber: string; bestBefore: string; url: string; settings: BatchLabelSettings;
}
export interface LabelPaperSettings { marginMm: number; gapMm: number }
export const DEFAULT_LABEL_PAPER: LabelPaperSettings = { marginMm: 5, gapMm: 1 };

/** Labels use the processed route's document ID, with the fresh-label date query convention. */
export function processedLabelUrl(productDocumentId: string, packingDate: string): string {
  if (!productDocumentId || productDocumentId === "." || productDocumentId === ".." || productDocumentId.includes("/") || !isValidDate(packingDate))
    throw new Error("Produktlänk eller packdatum saknas.");
  return `https://vert.farm/farm/products/processed/${encodeURIComponent(productDocumentId)}?date=${packingDate.slice(2).replace(/-/g, "")}`;
}

/** Physical stock includes held/reserved packages; printing never reserves or consumes them. */
export function activeStockLabelOptions(stock: LabelStock[], packages: ProcessedPackage[], products: LabelProduct[]): StockLabelOption[] {
  return stock.flatMap((entry) => {
    if (!Number.isSafeInteger(entry.quantity) || entry.quantity! <= 0 || entry.shopSaleStatus === "recalled") return [];
    const matches = packages.filter((pkg) => pkg.id === entry.packageId);
    if (matches.length !== 1) return [];
    const pkg = matches[0];
    const parents = products.filter((product) => product.id === pkg.parentId);
    if (pkg.packageType === "box" || !isProcessedPackageActive(pkg, packages) || parents.length !== 1 ||
      (entry.productId && entry.productId !== pkg.parentId)) return [];
    const boxes = packages.filter((candidate) => candidate.packageType === "box" && candidate.boundPackageId === pkg.id &&
      candidate.parentId === pkg.parentId && isProcessedPackageActive(candidate, packages) &&
      Number.isSafeInteger(candidate.unitsPerBox) && candidate.unitsPerBox > 0 && entry.quantity! >= candidate.unitsPerBox &&
      packages.filter((other) => other.id === candidate.id).length === 1);
    // A box is a print variant of the same lot, never a second stock pool.
    return [pkg, ...boxes].map((variant) => ({ stock: entry, pkg: variant, product: parents[0] }));
  }).sort((a, b) => a.product.name_sv.localeCompare(b.product.name_sv, "sv-SE") ||
    (b.stock.packingDate || "").localeCompare(a.stock.packingDate || ""));
}

export function prepareBatchLabels(options: StockLabelOption[], quantities: LabelQuantities): { labels: BatchPrintLabel[]; errors: string[] } {
  const labels: BatchPrintLabel[] = [], errors: string[] = [];
  const byId = new Map(options.map((option) => [stockLabelOptionId(option), option]));
  let total = 0;
  for (const [id, raw] of Object.entries(quantities)) {
    if (!raw || raw === "0") continue;
    const count = /^\d+$/.test(raw) ? Number(raw) : NaN;
    if (!Number.isSafeInteger(count) || count < 0 || count > 500) { errors.push("Ange ett heltal mellan 0 och 500 per förpackning och lagerpost."); continue; }
    total += count;
    if (total > MAX_BATCH_LABELS) { errors.push(`Välj högst ${MAX_BATCH_LABELS} etiketter per PDF.`); break; }
    const option = byId.get(id);
    if (!option) { errors.push("En vald lagerpost är inte längre aktiv. Ta bort den från urvalet."); continue; }
    const { stock, pkg, product } = option;
    if (!isPackagingLanguage(stock.packagingLanguage)) {
      errors.push(`${product.name_sv} · ${stock.batchId}: förpackningsspråk saknas i lagerposten. Välj svenska eller engelska i Processat lager.`); continue;
    }
    const language = stock.packagingLanguage;
    const prefix = `${product.name_sv} · ${stock.batchId} · ${language.toUpperCase()}: `;
    const originValue = language === "sv" ? stock.origin_sv : stock.origin_en;
    const origin = typeof originValue === "string" ? originValue.trim() : "";
    if (!origin) { errors.push(prefix + "ursprung saknas i lagerposten. Redigera lagerposten i Processat lager."); continue; }
    if (!isBatchLabelSettings(pkg.batchLabel)) { errors.push(prefix + "ange giltiga mått och färger för batch-etiketten på förpackningen."); continue; }
    if (!stock.packingDate || !isValidDate(stock.packingDate) || !stock.bestBeforeDate || !isValidDate(stock.bestBeforeDate) || stock.bestBeforeDate < stock.packingDate) {
      errors.push(prefix + "ange giltigt packdatum och bäst före i lagerposten."); continue;
    }
    try {
      const label: BatchPrintLabel = {
        stockId: stock.firebaseId, language, productName: language === "sv" ? product.name_sv : product.name_en,
        origin, batchNumber: typeof stock.batchId === "string" && stock.batchId.trim() ? stock.batchId.trim() : stock.packingDate.slice(2).replace(/-/g, ""), bestBefore: stock.bestBeforeDate,
        url: processedLabelUrl(product.firebaseId, stock.packingDate), settings: { ...pkg.batchLabel },
      };
      labelTypography(label); // Do not download labels with clipped mandatory text.
      for (let index = 0; index < count; index++) labels.push(label);
    } catch (error) { errors.push(prefix + (error instanceof Error ? error.message : "Etiketten kunde inte förberedas.")); }
  }
  if (!total && !errors.length) errors.push("Välj minst en etikett genom att ange antal för en lagerpost.");
  return { labels: errors.length ? [] : labels, errors: Array.from(new Set(errors)) };
}

const MM_TO_PT = 72 / 25.4;
export const mmToPt = (mm: number) => mm * MM_TO_PT;
function wrappedLines(value: string, columns: number): string[] {
  const result: string[] = [];
  let rest = value.replace(/\s+/g, " ").trim();
  while (rest.length > columns) {
    const space = rest.lastIndexOf(" ", columns);
    const length = space > 0 ? space : columns;
    result.push(rest.slice(0, length)); rest = rest.slice(length).trimStart();
  }
  if (rest) result.push(rest);
  return result;
}
/** Courier's fixed 600-unit glyph width makes wrapping deterministic in the PDF. */
export function labelTypography(label: BatchPrintLabel) {
  const { widthMm, heightMm } = label.settings;
  const qrMm = Math.min(24, heightMm - 5.2, widthMm * 0.4);
  const textWidthPt = mmToPt(widthMm - qrMm - 6);
  const availableHeightPt = mmToPt(heightMm - 5.2);
  const records = label.language === "sv" ? [`Ursprung: ${label.origin}`, `Batch: ${label.batchNumber}`, `Bäst före: ${label.bestBefore}`]
    : [`Origin: ${label.origin}`, `Batch: ${label.batchNumber}`, `Best before: ${label.bestBefore}`];
  for (let fontSize = 8; fontSize >= 5.5; fontSize -= 0.25) {
    const columns = Math.floor(textWidthPt / (fontSize * 0.6));
    if (columns < Math.max(10, label.batchNumber.length)) continue;
    const lines = records.flatMap((record) => wrappedLines(record, columns));
    if (lines.length * fontSize * 1.25 <= availableHeightPt) return { fontSize, lines, qrMm };
  }
  throw new Error("Texten ryms inte. Öka batch-etikettens bredd eller höjd.");
}

interface Rect { x: number; y: number; width: number; height: number }
export interface PlacedBatchLabel extends Rect { label: BatchPrintLabel; rotated: boolean; quarter: number }
const intersects = (a: Rect, b: Rect) => a.x < b.x + b.width - 0.00001 && a.x + a.width > b.x + 0.00001 && a.y < b.y + b.height - 0.00001 && a.y + a.height > b.y + 0.00001;
const contains = (a: Rect, b: Rect) => b.x >= a.x && b.y >= a.y && b.x + b.width <= a.x + a.width + 0.00001 && b.y + b.height <= a.y + a.height + 0.00001;
function splitFree(free: Rect[], used: Rect): Rect[] {
  const result = free.flatMap((rect) => {
    if (!intersects(rect, used)) return [rect];
    const parts: Rect[] = [];
    if (used.x > rect.x) parts.push({ ...rect, width: used.x - rect.x });
    if (used.x + used.width < rect.x + rect.width) parts.push({ ...rect, x: used.x + used.width, width: rect.x + rect.width - used.x - used.width });
    if (used.y > rect.y) parts.push({ ...rect, height: used.y - rect.y });
    if (used.y + used.height < rect.y + rect.height) parts.push({ ...rect, y: used.y + used.height, height: rect.y + rect.height - used.y - used.height });
    return parts;
  }).filter((rect) => rect.width > 0.00001 && rect.height > 0.00001);
  return result.filter((rect, index) => !result.some((other, otherIndex) =>
    (otherIndex < index && contains(other, rect)) || (otherIndex > index && contains(other, rect) && !contains(rect, other))));
}

/** MaxRects packing never crosses the quarter cuts. Multiple orders/scores improve mixed-size fit. */
export function arrangeBatchLabels(labels: BatchPrintLabel[], paper: LabelPaperSettings): PlacedBatchLabel[][] {
  if (!labels.length || labels.length > MAX_BATCH_LABELS || !Number.isFinite(paper.marginMm) || paper.marginMm < 0 || paper.marginMm > 15 ||
    !Number.isFinite(paper.gapMm) || paper.gapMm < 0 || paper.gapMm > 5) throw new Error("Kontrollera antal, pappersmarginal (0–15 mm) och mellanrum (0–5 mm).");
  const width = 105 - paper.marginMm - paper.gapMm / 2;
  const height = 148.5 - paper.marginMm - paper.gapMm / 2;
  for (const label of labels) {
    const { widthMm: w, heightMm: h } = label.settings;
    if (!isBatchLabelSettings(label.settings) || !((w <= width && h <= height) || (h <= width && w <= height)))
      throw new Error("En batch-etikett ryms inte inom ett A4-kvartal. Minska pappersmarginalen eller etikettmåtten.");
  }
  const byArea = [...labels].sort((a, b) => b.settings.widthMm * b.settings.heightMm - a.settings.widthMm * a.settings.heightMm);
  let best: PlacedBatchLabel[][] | undefined;
  for (const ordered of [byArea, labels]) for (const scoreKind of ["short", "area"] as const) for (const preferRotated of [false, true]) {
    const pages: PlacedBatchLabel[][] = [[]];
    let free = Array.from({ length: 4 }, () => [{ x: 0, y: 0, width: width + paper.gapMm, height: height + paper.gapMm }]);
    for (const label of ordered) {
      type Candidate = Rect & { quarter: number; rotated: boolean; score: number; distance: number };
      const find = (available: Rect[][]): Candidate | undefined => {
        const candidates: Candidate[] = [];
        available.forEach((rects, quarter) => rects.forEach((rect) => [false, true].forEach((rotated) => {
          const w = (rotated ? label.settings.heightMm : label.settings.widthMm) + paper.gapMm;
          const h = (rotated ? label.settings.widthMm : label.settings.heightMm) + paper.gapMm;
          if (w <= rect.width + 0.00001 && h <= rect.height + 0.00001) candidates.push({ x: rect.x, y: rect.y, width: w, height: h, quarter, rotated,
            score: scoreKind === "short" ? Math.min(rect.width - w, rect.height - h) : rect.width * rect.height - w * h,
            distance: rect.x * rect.x + rect.y * rect.y });
        })));
        return candidates.sort((a, b) => a.score - b.score || a.distance - b.distance ||
          Number(a.rotated !== preferRotated) - Number(b.rotated !== preferRotated) || a.quarter - b.quarter)[0];
      };
      let placement = find(free);
      if (!placement) { pages.push([]); free = Array.from({ length: 4 }, () => [{ x: 0, y: 0, width: width + paper.gapMm, height: height + paper.gapMm }]); placement = find(free)!; }
      free[placement.quarter] = splitFree(free[placement.quarter], placement);
      const w = placement.width - paper.gapMm, h = placement.height - paper.gapMm;
      const right = placement.quarter % 2 === 1, bottom = placement.quarter >= 2;
      pages[pages.length - 1].push({ label, width: w, height: h, quarter: placement.quarter, rotated: placement.rotated,
        x: right ? 105 + paper.gapMm / 2 + placement.x : 105 - paper.gapMm / 2 - placement.x - w,
        y: bottom ? 148.5 + paper.gapMm / 2 + placement.y : 148.5 - paper.gapMm / 2 - placement.y - h });
    }
    // Emit each page from the centre out, regardless of the size-packing order.
    pages.forEach((page) => page.sort((a, b) => Math.hypot(a.x + a.width / 2 - 105, a.y + a.height / 2 - 148.5) -
      Math.hypot(b.x + b.width / 2 - 105, b.y + b.height / 2 - 148.5) || a.quarter - b.quarter));
    const filledArea = (page: PlacedBatchLabel[]) => page.reduce((sum, placement) => sum + placement.width * placement.height, 0);
    const previousBest = best;
    const firstDifferentPage = previousBest && pages.findIndex((page, index) => Math.abs(filledArea(page) - filledArea(previousBest[index] || [])) > 0.00001);
    if (!best || pages.length < best.length || (pages.length === best.length && firstDifferentPage !== undefined && firstDifferentPage >= 0 &&
      filledArea(pages[firstDifferentPage]) > filledArea(best[firstDifferentPage]))) best = pages;
  }
  return best!;
}
